/**
 * Referral Detail Modal - Trade Loop v2.0
 *
 * Displays full referral details with status update functionality
 * Migrated from v1.0 with cookie-based authentication
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useRealtimeSubscription, type TableSubscription } from "@/hooks/useRealtimeSubscription";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Calendar,
  User,
  FileText,
  DollarSign,
  Paperclip,
  Phone,
  Mail,
  Check,
  X,
  CheckCircle,
  Star,
  ClipboardCheck,
  Clock,
  AlertTriangle,
  Zap,
  ImagePlus,
  Camera,
  Loader2,
  Users,
  Edit,
  Trash2,
  Pencil,
  MoreVertical,
  EyeOff,
  Eye,
  Send,
  AlertCircle,
  RotateCcw,
  Bell,
  MessageCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import ReferralCompletionModal from "./ReferralCompletionModal";
import AttachmentViewerModal from "./AttachmentViewerModal";
import EditReferralModal from "./EditReferralModal";
import ProfilePreviewModal from "./ProfilePreviewModal";
import ChangeReferralResponseModal from "./ChangeReferralResponseModal";
import ResendReferralModal from "./ResendReferralModal";
import { compressImage, isCompressibleImage } from "@/lib/imageUtils";
import { classifyMediaFile, MEDIA_ACCEPT_ATTRIBUTE, describeUploadError } from "@/lib/mediaUploadValidation";
import { mapWithConcurrency, REFERRAL_UPLOAD_CONCURRENCY, stabilizeAttachmentFile, uploadReferralAttachmentFile } from "@/lib/referralAttachmentUpload";
import { captureImageInto, isNativeCaptureAvailable } from "@/services/mediaCapture";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { usePublicProfile, toProfilePreviewPartner } from "@/api/extensions/usePublicProfile";
import { usePartnerConnection } from "@/api/extensions/usePartnerConnection";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/contexts/ToastContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const QUICK_REPLY_PRESETS = [
  'Thanks for the referral!',
  "Thanks for the referral! I'll follow up with the customer.",
];

interface ReferralDetailModalProps {
  referral: {
    id: number;
    senderId: string;
    receiverId: string;
    senderName?: string;
    senderTrade?: string;
    senderProfileImage?: string;
    receiverName?: string;
    receiverTrade?: string;
    receiverProfileImage?: string;
    // Bug 23: the other party permanently deleted their account. The referral (and its revenue)
    // survives; senderName/receiverName carry the neutral tombstone label and there is no profile
    // left to open.
    senderDeleted?: boolean;
    receiverDeleted?: boolean;
    senderFormerCompany?: string | null;
    receiverFormerCompany?: string | null;
    status: string;
    clientName?: string;
    customerName?: string;
    clientEmail?: string;
    clientPhone?: string;
    createdAt: string;
    completedAt?: string;
    referralAttachments?: string | null; // Database field: referral_attachments (transformed to camelCase)
    notes?: string;
    jobDescription?: string;
    jobLocation?: string;
    trades?: string[];
    customTrades?: string[];
    revenue?: number | string; // Revenue from completed referral
    // Availability request fields
    availabilityRequestId?: string | null;
    // Completion verification fields
    jobCompletedVerified?: boolean;
    customerSatisfiedVerified?: boolean;
    completionNotes?: string;
    outcome?: string; // Closure reason: 'completed', 'no_move_forward', 'no_contact', 'not_fit', 'other'
    verifiedBy?: string;
    verifiedAt?: string;
    [key: string]: any;
  };
  isOpen: boolean;
  onClose: () => void;
}

export default function ReferralDetailModal({ referral: referralProp, isOpen, onClose }: ReferralDetailModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();
  const queryClient = useQueryClient();

  // Subscribe to the canonical referral list so status mutations (accept,
  // decline, cancel, reopen, close-out, change-response) are reflected in
  // this modal immediately — without requiring the caller to re-pass props
  // or the user to refresh the page. Mutations below invalidate this key.
  //
  // NOTE: ReferralsPage also owns this same query key and reads it as
  // `{ success, data: [...] }` (raw fetch + json). The queryFn shape must
  // stay compatible — returning a bare array here would clobber the cache
  // and cause ReferralsPage's extractor to yield zero rows.
  const { data: liveReferralsResponse } = useQuery<any>({
    queryKey: ["/api/workflows/referral/list"],
    queryFn: async () => {
      const response = await fetch('/api/workflows/referral/list', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch referrals');
      }
      return response.json();
    },
    enabled: isOpen && !!user,
  });

  const referral = useMemo(() => {
    const list: any[] = Array.isArray(liveReferralsResponse)
      ? liveReferralsResponse
      : Array.isArray((liveReferralsResponse as any)?.data)
        ? (liveReferralsResponse as any).data
        : [];
    if (list.length > 0) {
      const fresh = list.find((r: any) => r.id === referralProp.id);
      if (fresh) return { ...referralProp, ...fresh };
    }
    return referralProp;
  }, [liveReferralsResponse, referralProp]);

  // Hide referral mutation (soft-delete - adds user to hidden_for array)
  const hideMutation = useMutation({
    mutationFn: async (referralId: number) => {
      const response = await fetch(`/api/workflows/referral/${referralId}/delete`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to hide referral');
      }

      return response.json();
    },
    onSuccess: () => {
      showSuccess('Referral hidden', 'The referral has been hidden from your view and no longer counts towards your active referrals.');
      queryClient.invalidateQueries({ queryKey: ["/api/referrals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });
      onClose(); // Close the modal after hiding
    },
    onError: (error: Error) => {
      showError('Error', error.message || 'Failed to hide referral. Please try again.');
    },
  });

  // Unhide referral mutation (removes user from hidden_for array)
  const unhideMutation = useMutation({
    mutationFn: async (referralId: number) => {
      const response = await fetch(`/api/workflows/referral/${referralId}/unhide`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to unhide referral');
      }

      return response.json();
    },
    onSuccess: () => {
      showSuccess('Referral restored', 'The referral is now visible in your normal view again.');
      queryClient.invalidateQueries({ queryKey: ["/api/referrals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });
      onClose(); // Close the modal after unhiding
    },
    onError: (error: Error) => {
      showError('Error', error.message || 'Failed to unhide referral. Please try again.');
    },
  });
  const [attachmentUrls, setAttachmentUrls] = useState<Record<number, string>>({});
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showChangeResponseModal, setShowChangeResponseModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newMessage, setNewMessage] = useState('');
  // Quick-reply card shown to the receiver only in the moment right after THEY accept in this
  // session. Pure local state (no DB flag): set by the successful accept below, cleared whenever
  // the panel closes or switches referral, so reopening, refreshing or any other viewer never sees it.
  const [quickReply, setQuickReply] = useState<
    { referralId: number; phase: 'offer' } | { referralId: number; phase: 'sent'; text: string } | null
  >(null);
  // Sender Activity: only the latest line until the user asks for the full history.
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  const [showUnhideConfirm, setShowUnhideConfirm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Message edit/delete state (own messages only — mirrors the conversation window)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  // Pending destructive actions awaiting confirmation
  const [attachmentPendingDelete, setAttachmentPendingDelete] = useState<any | null>(null);
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [profilePreviewPartnerId, setProfilePreviewPartnerId] = useState<string | null>(null);

  // Fetch the partner's public profile when their name/avatar is clicked.
  // Shared hook with the messages page — same data shape /network renders.
  const { data: partnerProfileData } = usePublicProfile(
    profilePreviewPartnerId,
    showProfilePreview
  );

  // Connection record for the referral counterparty — drives the favorite
  // button and hamburger menu on the profile preview.
  const partnerConnection = usePartnerConnection(profilePreviewPartnerId);

  // Calculate edit/delete permissions
  const currentUserId = user?.id;
  const isSent = referral?.senderId === currentUserId;
  const isReceiver = referral?.receiverId === currentUserId;
  // Both sender and receiver can hide referrals
  const canDelete = isSent || isReceiver;
  // Only sender can edit, and only if not completed
  const canEdit = isSent && referral.status?.toLowerCase() !== 'completed';
  // Check if the referral is hidden by the current user
  const isHiddenByCurrentUser = Array.isArray(referral.hiddenFor) && currentUserId
    ? referral.hiddenFor.includes(currentUserId)
    : false;

  // Check if user is an organization member (not admin/owner)
  const isOrgMember = user?.organizationMembership?.role === 'member' ||
                      user?.organizationMembership?.role === 'member';

  // Record the receiver's first view so the sender sees "Viewed". The API accepts it only from
  // the receiver and keeps the first view, so a repeat call is harmless.
  const receiverFirstViewedAt = referral.receiverFirstViewedAt;
  useEffect(() => {
    if (!isOpen || !isReceiver || !referral.id || receiverFirstViewedAt) return;
    api.post(`/api/workflows/referral/${referral.id}/viewed`)
      .then(() => queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] }))
      .catch((error) => console.warn('[ReferralDetailModal] Failed to record view:', error));
  }, [isOpen, isReceiver, referral.id, receiverFirstViewedAt, queryClient]);

  // Fetch group details if this referral is part of a group
  const groupId = referral.referral_group_id || referral.referralGroupId;

  const { data: groupDetails, isLoading: isLoadingGroup, error: groupError } = useQuery({
    queryKey: [`/api/workflows/referral-groups/${groupId}`],
    enabled: !!groupId, // Only run if groupId exists
    queryFn: async () => {
      if (!groupId) return null;

      const response = await fetch(`/api/workflows/referral-groups/${groupId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch group details');
      }

      return await response.json();
    }
  });

  // Calculate group progress for display
  const groupProgress = useMemo(() => {
    if (!groupDetails?.referrals) return null;

    const acceptedCount = groupDetails.referrals.filter(
      (r: any) => r.statusName === 'accepted' || r.statusName === 'completed'
    ).length;
    const totalCount = groupDetails.referrals.length;
    const isSingleAccept = groupDetails?.group?.isSingleAccept;
    const hasAccepted = acceptedCount > 0;

    // For single-accept: 100% if anyone accepted, 0% otherwise
    // For multi-accept: actual ratio
    const progressPercent = isSingleAccept
      ? (hasAccepted ? 100 : 0)
      : (acceptedCount / totalCount) * 100;

    const progressText = isSingleAccept
      ? (hasAccepted ? 'Job accepted' : 'Waiting for acceptance')
      : `${acceptedCount} of ${totalCount} accepted`;

    return { progressPercent, progressText };
  }, [groupDetails]);

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const response = await api.put(`/api/workflows/referral/${id}/status`, { status });
      return response;
    },
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });
      // MessagesPage and ProfilePreviewModal cache under ["referrals", ...] keys.
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
      showSuccess("Status updated", "Referral status has been updated successfully.");
      // Group referrals have no single partner thread (and the group endpoint is gone), so no card.
      if (variables.status === 'accepted' && isReceiver && !groupId) {
        setQuickReply({ referralId: variables.id, phase: 'offer' });
      }
    },
    onError: (error: any) => {
      showError("Error", error.message || "Failed to update status");
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({ referralId, message }: { referralId: number; message: string }) => {
      const response = await api.post(`/api/workflows/referral/${referralId}/message`, { message });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workflows/referral/${referral.id}/messages`] });
      setNewMessage('');
    },
    onError: (error: any) => {
      showError("Error", error.message || "Failed to send message");
    },
  });

  // Quick reply after accepting: same send endpoint as the chat input, but posted to the referral
  // that was just accepted (captured at accept time), with the message shown in the thread at once.
  const quickReplyMutation = useMutation({
    mutationFn: async ({ referralId, message }: { referralId: number; message: string }) =>
      api.post(`/api/workflows/referral/${referralId}/message`, { message }),
    onMutate: async ({ referralId, message }) => {
      const key = [`/api/workflows/referral/${referralId}/messages`];
      // Stop an in-flight poll from overwriting the optimistic message.
      await queryClient.cancelQueries({ queryKey: key });
      const optimisticId = `quick-reply-pending-${Date.now()}`;
      queryClient.setQueryData(key, (old: any) => ({
        unreadCount: 0,
        ...old,
        messages: [
          ...(old?.messages ?? []),
          {
            id: optimisticId,
            content: message,
            isOwn: true,
            pending: true,
            timestamp: new Date().toISOString(),
          },
        ],
      }));
      return { key, optimisticId };
    },
    onError: (error: any, { referralId }, context) => {
      if (context) {
        queryClient.setQueryData(context.key, (old: any) =>
          old ? { ...old, messages: old.messages.filter((m: any) => m.id !== context.optimisticId) } : old
        );
      }
      // Restore the card, unless the panel was closed meanwhile (state already cleared).
      setQuickReply((current) =>
        current && current.referralId === referralId ? { referralId, phase: 'offer' } : current
      );
      showError("Error", error.message || "Failed to send message");
    },
    onSettled: (_data, _error, { referralId }) => {
      queryClient.invalidateQueries({ queryKey: [`/api/workflows/referral/${referralId}/messages`] });
    },
  });

  // Edit an own message in this referral thread. Referral messages are ordinary
  // chat_messages, so this reuses the shared sender-only message edit endpoint.
  const editMessageMutation = useMutation({
    mutationFn: async ({ messageId, content }: { messageId: string; content: string }) => {
      return api.put(`/api/workflows/messages/${messageId}/edit`, { messageContent: content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workflows/referral/${activeReferralId}/messages`] });
      setEditingMessageId(null);
      setEditContent('');
      // No success toast: the edited text (and its "edited" marker) is its own confirmation.
    },
    onError: (error: any) => {
      showError('Error', error?.message || 'Failed to edit message');
    },
  });

  // Delete an own message (hard delete for everyone; sender-only, enforced by the API).
  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return api.delete(`/api/workflows/messages/single/${messageId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/workflows/referral/${activeReferralId}/messages`] });
      // No success toast: the message vanishing from the thread is its own confirmation.
    },
    onError: (error: any) => {
      showError('Error', error?.message || 'Failed to delete message');
    },
  });

  // Delete a referral attachment (removes the row entry AND the R2 object).
  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachment: any) => {
      return api.delete(`/api/workflows/referral/${referral.id}/attachment`, { path: attachment.path });
    },
    onSuccess: (_res, attachment) => {
      const removedIndex = attachmentsList.findIndex((att) => att.path === attachment.path);
      const updated = attachmentsList.filter((att) => att.path !== attachment.path);
      setAttachmentsList(updated);
      // Every index past the removed one shifts down by one. Remap the already
      // resolved URLs instead of clearing them, so the viewer shows the next
      // attachment immediately rather than flashing the generic file fallback.
      setAttachmentUrls((prev) => {
        const remapped: Record<number, string> = {};
        updated.forEach((_att, i) => {
          const from = removedIndex >= 0 && i >= removedIndex ? i + 1 : i;
          if (prev[from]) remapped[i] = prev[from];
        });
        return remapped;
      });
      setShowAllAttachments(false);
      setAttachmentPendingDelete(null);
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      showSuccess('Attachment deleted', 'The file has been removed from this referral.');
    },
    onError: (error: any) => {
      setAttachmentPendingDelete(null);
      showError('Error', error?.message || 'Failed to delete attachment');
    },
  });

  // Only the uploader may delete their own attachment. Legacy attachments with no
  // uploader recorded are attributed to the referral sender (matches the backend).
  const canDeleteAttachment = (attachment: any): boolean =>
    attachment?.uploadedBy ? attachment.uploadedBy === currentUserId : isSent;

  const handleStatusUpdate = (status: string) => {
    updateStatusMutation.mutate({ id: referral.id, status });
  };

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    // Sending your own message always snaps the thread to the bottom, even if
    // you'd scrolled up — mark yourself as "at the bottom" so the scroll effect
    // follows the new message down once it lands.
    isAtBottomRef.current = true;
    sendMessageMutation.mutate({ referralId: referral.id, message: newMessage.trim() });
  };

  const handleQuickReply = (text: string) => {
    if (quickReply?.phase !== 'offer') return;
    // Like a normal send, snap the thread to the new message.
    isAtBottomRef.current = true;
    // The card is replaced at once; onError puts it back.
    setQuickReply({ referralId: quickReply.referralId, phase: 'sent', text });
    quickReplyMutation.mutate({ referralId: quickReply.referralId, message: text });
  };

  const handleMessageKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleAttachmentClick = (index: number) => {
    setViewerInitialIndex(index);
    setViewerOpen(true);
  };

  const handleShareAttachment = async (type: 'sms' | 'email' | 'download', attachment: any, url: string) => {
    const isImage = attachment.contentType?.startsWith('image/') ||
      /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(attachment.name || '');

    if (type === 'sms') {
      // For SMS, use Web Share API if available (supports images on mobile)
      if (navigator.share && isImage) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const file = new File([blob], attachment.name, { type: attachment.contentType || 'image/jpeg' });

          await navigator.share({
            title: `Referral Attachment: ${attachment.name}`,
            text: `Attachment from referral for ${clientName}`,
            files: [file]
          });
          showSuccess('Shared successfully', 'Attachment shared via SMS');
        } catch (error) {
          // Fallback to copying link if share fails or is cancelled
          navigator.clipboard.writeText(url);
          showSuccess('Link copied', 'Attachment link copied to clipboard. Paste in your messaging app.');
        }
      } else {
        // Fallback: copy link to clipboard
        navigator.clipboard.writeText(url);
        showSuccess('Link copied', 'Attachment link copied to clipboard. Paste in your SMS app.');
      }
    } else if (type === 'email') {
      // For email, use Web Share API if available, otherwise download and instruct user
      if (navigator.share && isImage) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const file = new File([blob], attachment.name, { type: attachment.contentType || 'image/jpeg' });

          await navigator.share({
            title: `Referral Attachment: ${attachment.name}`,
            text: `Attachment from referral for ${clientName}`,
            files: [file]
          });
          showSuccess('Shared successfully', 'Attachment shared via email');
        } catch (error) {
          // Fallback to mailto with instructions
          const subject = encodeURIComponent(`Referral Attachment: ${attachment.name}`);
          const body = encodeURIComponent(
            `Here is the attachment from referral for ${clientName}:\n\n` +
            `File: ${attachment.name}\n` +
            `Link: ${url}\n\n` +
            `Note: Please download the file from the link above and attach it to your email.`
          );
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
        }
      } else {
        // For non-images or no share API, open mailto with link
        const subject = encodeURIComponent(`Referral Attachment: ${attachment.name}`);
        const body = encodeURIComponent(
          `Here is the attachment from referral for ${clientName}:\n\n` +
          `File: ${attachment.name}\n` +
          `Link: ${url}\n\n` +
          `Note: Please download the file from the link above and attach it to your email.`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":   return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "accepted":  return "bg-green-100 text-green-800 border-green-200";
      case "completed": return "bg-blue-100 text-blue-800 border-blue-200";
      case "declined":  return "bg-red-100 text-red-800 border-red-200";
      default:          return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getModalBgStyle = (status: string) => {
    switch (status?.toLowerCase()) {
      case "pending":   return "bg-yellow-50/95";
      case "accepted":  return "bg-green-50/95";
      case "completed": return "bg-blue-50/95";
      case "declined":  return "bg-red-50/95";
      default:          return "bg-white/95";
    }
  };

  const getUrgencyBadgeStyle = (urgency: string) => {
    switch (urgency?.toLowerCase()) {
      case "urgent": return "bg-red-100 text-red-800 border-red-200";
      case "high":   return "bg-orange-100 text-orange-800 border-orange-200";
      case "normal": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "flexible":
      case "low":    return "bg-green-100 text-green-800 border-green-200";
      default:       return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency?.toLowerCase()) {
      case "urgent": return <Zap className="w-3.5 h-3.5" />;
      case "high":   return <AlertTriangle className="w-3.5 h-3.5" />;
      case "normal": return <Clock className="w-3.5 h-3.5" />;
      case "flexible":
      case "low":    return <Check className="w-3.5 h-3.5" />;
      default:       return null;
    }
  };

  const getUrgencyLabel = (urgency: string) => {
    switch (urgency?.toLowerCase()) {
      case "urgent":   return "Urgent";
      case "high":     return "High Priority";
      case "normal":   return "Normal";
      case "flexible": return "Flexible";
      case "low":      return "Flexible";
      default:         return urgency?.charAt(0).toUpperCase() + urgency?.slice(1);
    }
  };

  const formatValue = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  // Determine referral direction (currentUserId and isSent already declared above)
  const partnerName = isSent ? referral.receiverName : referral.senderName;
  const partnerTrade = isSent ? referral.receiverTrade : referral.senderTrade;
  const partnerId = isSent ? referral.receiverId : referral.senderId;
  // Bug 23: the referral itself survives the partner deleting their account, so partnerName is
  // already the neutral "Former Trade Loop User" label from the backend. There is no profile
  // left to open, though, so the avatar and the name stop being links.
  const partnerDeleted = Boolean(isSent ? referral.receiverDeleted : referral.senderDeleted);
  const partnerFormerCompany = isSent ? referral.receiverFormerCompany : referral.senderFormerCompany;
  const clientName = referral.clientName || referral.customerName || "Contact";

  // Show partner's profile image
  const profileImageUrl = isSent ? referral.receiverProfileImage : referral.senderProfileImage;

  const openPartnerProfile = () => {
    if (!partnerId || partnerDeleted) return;
    setProfilePreviewPartnerId(partnerId);
    setShowProfilePreview(true);
  };

  // Determine which status to display
  // For group referrals (sent by this user), show group status instead of individual status
  const displayStatus = referral.groupStatus || referral.status;
  const isShowingGroupStatus = !!referral.groupStatus;

  // Parse attachments helper
  const parseAttachments = (raw: any): any[] => {
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  };

  // Parse attachments from referralAttachments field (database column: referral_attachments)
  const [attachmentsList, setAttachmentsList] = useState<any[]>(() => parseAttachments(referral.referralAttachments));
  const [showAllAttachments, setShowAllAttachments] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const MAX_COLS = 6;

  // For group referrals, track which partner's messages to show
  const [selectedPartnerIndex, setSelectedPartnerIndex] = useState(0);

  // Derive chatId from the two participants (same logic as backend)
  const chatId = useMemo(() => {
    if (groupId && groupDetails?.referrals) {
      // For group referrals, get chatId for selected partner
      const selectedReferral = groupDetails.referrals[selectedPartnerIndex];
      if (!selectedReferral || !referral.senderId) return null;
      return [referral.senderId, selectedReferral.receiverId].sort().join('_');
    }
    // For individual referrals
    if (!referral.senderId || !referral.receiverId) return null;
    return [referral.senderId, referral.receiverId].sort().join('_');
  }, [referral.senderId, referral.receiverId, groupId, groupDetails, selectedPartnerIndex]);

  // For group referrals, get the specific referral ID for the selected partner
  const activeReferralId = useMemo(() => {
    if (groupId && groupDetails?.referrals) {
      return groupDetails.referrals[selectedPartnerIndex]?.id;
    }
    return referral.id;
  }, [groupId, groupDetails, selectedPartnerIndex, referral.id]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // The modal's own scrolling body, the Referral Chat section inside it, and the message
  // input. scrollToReferralChat() below drives all three.
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const chatSectionRef = useRef<HTMLDivElement | null>(null);
  // The dialog content mounts after this component's first commit (portal), so the observer
  // below waits for the section element rather than a ref that is still empty.
  const [chatSectionEl, setChatSectionEl] = useState<HTMLDivElement | null>(null);
  const setChatSection = useCallback((el: HTMLDivElement | null) => {
    chatSectionRef.current = el;
    setChatSectionEl(el);
  }, []);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [isChatInView, setIsChatInView] = useState(false);
  // Track whether the user is scrolled to (near) the bottom of the thread so a
  // background refresh only auto-scrolls when they're already following along.
  const isAtBottomRef = useRef(true);
  // Distinguish "opened/switched referral" (jump to bottom) from "new message
  // arrived" (only follow if already at the bottom).
  const prevThreadKeyRef = useRef<string | null>(null);
  const prevThreadCountRef = useRef(0);

  const { data: threadData } = useQuery({
    queryKey: [`/api/workflows/referral/${activeReferralId}/messages`],
    queryFn: async () => {
      const res = await api.get(`/api/workflows/referral/${activeReferralId}/messages`);
      // `unreadCount` rides along on the same response (no extra request or poll).
      return {
        messages: Array.isArray(res.data) ? res.data : [],
        unreadCount: Number(res.unreadCount) || 0,
      };
    },
    enabled: isOpen && !!activeReferralId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    // Fallback poll while the modal is open so the other participant's new
    // messages appear within a few seconds even if the realtime push is missed.
    // React Query pauses this automatically when the tab is not focused.
    refetchInterval: isOpen && activeReferralId ? 5000 : false,
  });

  const threadMessages: any[] = threadData?.messages ?? [];
  const unreadChatCount: number = threadData?.unreadCount ?? 0;

  // Live updates while the modal is open. Supabase realtime is the instant path;
  // the query polls above are the safety net if a postgres_changes event is
  // missed. We subscribe to the referral row (photos/attachments live on the
  // referral_attachments JSONB column) and to this thread's chat_messages.
  const realtimeTables = useMemo<TableSubscription[]>(() => {
    const tables: TableSubscription[] = [];
    if (referral?.id) {
      tables.push({ table: 'referrals', event: 'UPDATE', filter: `id=eq.${referral.id}` });
    }
    if (activeReferralId) {
      tables.push({ table: 'chat_messages', event: '*', filter: `referral_id=eq.${activeReferralId}` });
    }
    return tables;
  }, [referral?.id, activeReferralId]);

  const handleRealtimeChange = useCallback(
    (table: string, payload: { new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
      if (table === 'referrals') {
        // Merge the canonical attachment list from the updated row so a photo
        // another participant just added shows up without a refresh. Only touch
        // state when the column is actually present in the payload (it is, with
        // REPLICA IDENTITY FULL) so a partial event can never wipe the list.
        const raw = (payload?.new as any)?.referral_attachments;
        if (raw !== undefined) {
          setAttachmentsList(parseAttachments(raw));
        }
        queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      } else if (table === 'chat_messages') {
        queryClient.invalidateQueries({
          queryKey: [`/api/workflows/referral/${activeReferralId}/messages`],
        });
      }
    },
    [activeReferralId, queryClient]
  );

  useRealtimeSubscription({
    channelName: `referral-modal-${referral?.id ?? 'none'}-${activeReferralId ?? 'none'}`,
    enabled: isOpen && realtimeTables.length > 0,
    tables: realtimeTables,
    onPostgresChange: handleRealtimeChange,
  });

  // Keep the thread scrolled sensibly WITHOUT ever yanking the user away from
  // where they're reading (scrolls only within the messages container, never the
  // whole modal). Two distinct cases:
  //   1. Modal opens / referral switches → jump straight to the latest message.
  //   2. New message(s) arrive (send, realtime push, or poll fallback) → only
  //      follow to the bottom if the user was already at the bottom. If they've
  //      scrolled up to read history, leave their position untouched — the
  //      browser keeps it naturally since content is appended below the viewport.
  useEffect(() => {
    const count = threadMessages.length;
    const threadKey = isOpen ? String(referral?.id ?? 'none') : null;
    const threadChanged = prevThreadKeyRef.current !== threadKey;

    if (!isOpen || count === 0) {
      prevThreadKeyRef.current = threadKey;
      prevThreadCountRef.current = count;
      return;
    }

    if (threadChanged) {
      // Fresh open / switch: land at the newest message instantly.
      isAtBottomRef.current = true;
      setTimeout(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 100);
    } else if (count > prevThreadCountRef.current && isAtBottomRef.current) {
      // New message while already following the thread — pin to the bottom.
      // Instant (not smooth) on purpose: a smooth animation fires intermediate
      // onScroll events that momentarily read as "not at bottom" and flip
      // isAtBottomRef off, so a quick follow-up message would then fail to
      // follow. Scroll the container itself (not scrollIntoView, which would
      // also move the modal/page) so nothing outside the thread ever moves.
      setTimeout(() => {
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      }, 100);
    }

    prevThreadKeyRef.current = threadKey;
    prevThreadCountRef.current = count;
  }, [isOpen, threadMessages.length, referral?.id]);

  // Record whether the user is at (or near) the bottom of the thread on every
  // scroll, so the effect above knows whether an incoming message should follow.
  const handleThreadScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // 80px of slack so "almost at the bottom" still counts as following along.
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= 80;
  };

  // Fresh slate whenever the panel opens/closes or shows another referral.
  useEffect(() => {
    setQuickReply(null);
  }, [isOpen, referral.id]);

  // Scroll the modal's own body (never the page) so the Referral Chat section is in view,
  // optionally focusing the message input. Exposed as a plain function so other actions in
  // this modal (e.g. a quick-reply after accepting) can reuse it.
  const scrollToReferralChat = useCallback(({ focusInput = false }: { focusInput?: boolean } = {}) => {
    const scrollBodyToChat = () => {
      const body = modalBodyRef.current;
      const section = chatSectionRef.current;
      if (!body || !section) return;
      const top =
        body.scrollTop + (section.getBoundingClientRect().top - body.getBoundingClientRect().top) - 12;
      if (typeof body.scrollTo === 'function') {
        body.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else {
        body.scrollTop = Math.max(0, top);
      }
    };
    if (focusInput) {
      // Focus synchronously: iOS (WKWebView) only raises the keyboard for a focus() made inside the
      // user's tap, not one deferred with setTimeout. preventScroll leaves positioning to the scroll.
      chatInputRef.current?.focus({ preventScroll: true });
    }
    scrollBodyToChat();
    if (focusInput) {
      // The keyboard resizing the viewport can leave the chat off position; settle it once more.
      setTimeout(scrollBodyToChat, 350);
    }
  }, []);

  // Messages stay unread until the chat is actually on screen. The server scopes this call
  // to messages the caller RECEIVED in this one referral's thread.
  const markThreadReadMutation = useMutation({
    mutationFn: async ({ chatId: threadChatId, referralId }: { chatId: string; referralId: number }) =>
      api.patch(`/api/workflows/messages/${threadChatId}::ref-${referralId}/mark-read`),
    onSuccess: (_res, { referralId }) => {
      queryClient.setQueryData([`/api/workflows/referral/${referralId}/messages`], (old: any) =>
        old ? { ...old, unreadCount: 0 } : old
      );
      queryClient.invalidateQueries({ queryKey: ["unreadMessages"], refetchType: 'active' });
    },
  });

  const markChatRead = useCallback(() => {
    if (!chatId || !activeReferralId || markThreadReadMutation.isPending) return;
    markThreadReadMutation.mutate({ chatId, referralId: activeReferralId });
  }, [chatId, activeReferralId, markThreadReadMutation]);

  const handleReferralChatClick = () => {
    scrollToReferralChat();
    if (unreadChatCount > 0) markChatRead();
  };

  // Track whether the chat section is visible inside the modal body.
  useEffect(() => {
    const body = modalBodyRef.current;
    const section = chatSectionEl;
    if (!isOpen || !body || !section || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsChatInView(entry.isIntersecting),
      { root: body, threshold: 0.3 }
    );
    observer.observe(section);
    return () => {
      observer.disconnect();
      setIsChatInView(false);
    };
  }, [isOpen, chatSectionEl]);

  // Chat on screen with unread messages (including ones that arrive while it is open) = read.
  useEffect(() => {
    if (isOpen && isChatInView && unreadChatCount > 0) markChatRead();
    // markChatRead changes identity with the mutation state; key off the count and visibility.
  }, [isOpen, isChatInView, unreadChatCount, activeReferralId]);

  // Re-sync attachments if the referral prop changes (e.g. parent refetches)
  useEffect(() => {
    setAttachmentsList(parseAttachments(referral.referralAttachments));
  }, [referral.referralAttachments]);

  // Poll the referral row while the modal is open as a fallback for the realtime
  // `referrals` UPDATE event, which this environment doesn't reliably deliver.
  // Mirrors the message thread's 5s poll so media another participant adds shows
  // up within a few seconds instead of only after closing/reopening the modal.
  const { data: polledAttachments } = useQuery({
    queryKey: [`referral-attachment-poll-${referral?.id}`],
    queryFn: async () => {
      const res = await api.get(`/api/workflows/referral/${referral.id}`);
      const raw = (res?.data as any)?.referralAttachments ?? (res?.data as any)?.referral_attachments;
      return parseAttachments(raw);
    },
    enabled: isOpen && !!referral?.id,
    refetchOnWindowFocus: false,
    refetchInterval: isOpen && referral?.id ? 5000 : false,
  });

  // Adopt the polled list only when its set of files actually changed, so we
  // don't churn a re-render every tick or briefly fight an optimistic add.
  useEffect(() => {
    if (!polledAttachments) return;
    setAttachmentsList((prev) => {
      const unchanged =
        prev.length === polledAttachments.length &&
        prev.every((a, i) => a?.path === polledAttachments[i]?.path);
      return unchanged ? prev : polledAttachments;
    });
  }, [polledAttachments]);

  // Reset expansion only when switching to a different referral or closing the modal
  useEffect(() => {
    setShowAllAttachments(false);
    setShowFullDescription(false);
    setShowAllActivity(false);
    setSelectedPartnerIndex(0); // Reset to first partner when referral changes
  }, [referral.id]);

  useEffect(() => {
    if (!isOpen) {
      setShowAllAttachments(false);
      setShowFullDescription(false);
      setShowAllActivity(false);
      setSelectedPartnerIndex(0); // Reset when modal closes
    }
  }, [isOpen]);

  // Generate fresh signed URLs whenever the modal opens or the list changes
  useEffect(() => {
    if (!isOpen || attachmentsList.length === 0) {
      setAttachmentUrls({});
      return;
    }

    const generateUrls = async () => {
      const urls: Record<number, string> = {};

      for (let i = 0; i < attachmentsList.length; i++) {
        const attachment = attachmentsList[i];

        if (attachment.path) {
          try {
            const response = await api.post('/api/files/generate-signed-url', {
              bucket: attachment.bucket || 'referral-attachments',
              path: attachment.path,
              expiresIn: 3600,
            });
            if (response?.data?.url) urls[i] = response.data.url;
          } catch (error) {
            console.error('[ReferralDetailModal] Failed to generate signed URL:', error);
          }
        } else if (attachment.url) {
          urls[i] = attachment.url;
        }
      }

      setAttachmentUrls(prev => ({ ...prev, ...urls }));
    };

    generateUrls();
  }, [isOpen, attachmentsList.length]);

  // Upload photo(s)/video(s) and append them to this referral's attachments (max 20 total)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    await uploadSelectedFiles(Array.from(files));
  };

  // Shared by the web file input and the native camera — both hand over plain Files.
  const uploadSelectedFiles = async (files: File[]) => {
    const MAX_FILES = 20;
    const currentCount = attachmentsList.length;
    const availableSlots = MAX_FILES - currentCount;

    if (availableSlots <= 0) {
      showError(
        "Maximum Files Reached",
        `You can only upload up to ${MAX_FILES} files. Please remove some files first.`
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Limit the number of files to process
    const filesToProcess = files.slice(0, availableSlots);

    if (files.length > availableSlots) {
      showWarning(
        "Some Files Skipped",
        `Only ${availableSlots} file(s) can be added. ${files.length - availableSlots} file(s) were skipped.`
      );
    }

    // Classify files by MIME type, falling back to file extension when the
    // browser fails to identify the type (common for .mov on Windows/Linux).
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const rawFile of filesToProcess) {
      const { kind, file } = classifyMediaFile(rawFile);

      if (kind === 'unknown') {
        invalidFiles.push(`${rawFile.name} (invalid type)`);
        continue;
      }

      const isVideo = kind === 'video';

      // Validate file size (25MB for images, 250MB for videos)
      const maxSize = isVideo ? 250 * 1024 * 1024 : 25 * 1024 * 1024;
      const maxSizeLabel = isVideo ? "250MB" : "25MB";

      if (file.size > maxSize) {
        // Auto-compress images over the limit
        if (!isVideo && isCompressibleImage(file)) {
          try {
            // Compress only far enough to fit the 25MB cap, not down to it. The copy comes
            // first: decoding a picker handle can fail on Android (stabilizeAttachmentFile).
            const compressed = await compressImage(await stabilizeAttachmentFile(file), 24, 0.85);
            validFiles.push(compressed);
            continue;
          } catch {
            invalidFiles.push(`${file.name} (compression failed)`);
            continue;
          }
        }
        invalidFiles.push(`${file.name} (exceeds ${maxSizeLabel})`);
        continue;
      }

      validFiles.push(file);
    }

    if (invalidFiles.length > 0) {
      showError(
        "Some Files Invalid",
        `Skipped: ${invalidFiles.join(", ")}`
      );
    }

    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);

    // Upload all valid files, a few at a time, straight to storage
    const uploadedAttachments: typeof attachmentsList = [];
    const newUrls: Record<number, string> = {};
    const failedFiles: { name: string; reason: string }[] = [];

    const results = await mapWithConcurrency(validFiles, REFERRAL_UPLOAD_CONCURRENCY, (file) =>
      uploadReferralAttachmentFile(file)
    );

    results.forEach((result, i) => {
      const file = validFiles[i];
      if (result.status === 'fulfilled') {
        const uploaded = result.value;
        const newAttachment = {
          path: uploaded.path,
          name: uploaded.fileName,
          bucket: 'referral-attachments',
          contentType: uploaded.contentType,
          uploadedBy: currentUserId, // track uploader so only they can delete it
        };

        uploadedAttachments.push(newAttachment);

        // Store signed URL for immediate preview
        if (uploaded.url) {
          const newIndex = currentCount + uploadedAttachments.length - 1;
          newUrls[newIndex] = uploaded.url;
        }
      } else {
        const error: any = result.reason;
        const apiErr = error instanceof ApiError ? error : undefined;
        failedFiles.push({
          name: file.name,
          reason: describeUploadError(apiErr ?? { message: error?.message }, file.name),
        });
      }
    });

    // Update state with all uploaded files at once
    if (uploadedAttachments.length > 0) {
      const updated = [...attachmentsList, ...uploadedAttachments];
      setAttachmentsList(updated);
      setAttachmentUrls(prev => ({ ...prev, ...newUrls }));

      // Persist ONLY the newly uploaded attachments via the additive endpoint.
      // Sending just the new items (instead of PUT-ing the whole array) prevents
      // the last-write-wins race where this modal's stale copy would clobber
      // attachments another participant added while both had the modal open.
      try {
        const res = await api.post(`/api/workflows/referral/${referral.id}/attachment`, {
          attachments: uploadedAttachments,
        });

        // The endpoint returns the canonical, server-merged list — adopt it so we
        // also reflect any attachments a concurrent uploader added.
        const canonical = parseAttachments(res?.data);
        if (canonical.length > 0) {
          setAttachmentsList(canonical);
        }

        queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });

        if (uploadedAttachments.length === 1) {
          showSuccess('File uploaded', 'Your file has been added to this referral.');
        } else {
          showSuccess('Files uploaded', `${uploadedAttachments.length} files have been added to this referral.`);
        }
      } catch (error: any) {
        showError('Save failed', 'Files uploaded but failed to save to referral');
      }
    }

    if (failedFiles.length > 0) {
      const firstReason = failedFiles[0].reason;
      const suffix = failedFiles.length > 1 ? ` (${failedFiles.length} files failed)` : '';
      showError('Upload Failed', `${firstReason}${suffix}`);
    }

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl p-0 gap-0 overflow-hidden max-h-[80vh] flex flex-col">

        {/* ── Header ── */}
        <div className={`px-5 pt-5 pb-4 flex-shrink-0 relative ${getModalBgStyle(displayStatus)}`}>
          {/* Hamburger menu — items vary by role/status. Render whenever at
              least one item applies; the items themselves enforce the rules. */}
          {(() => {
            const showEdit = canEdit;
            const showHide = canDelete && !isHiddenByCurrentUser;
            const showUnhide = canDelete && isHiddenByCurrentUser;
            const showReopen = displayStatus === 'cancelled' && isSent && referral.senderId === currentUserId;
            const showChangeResponse = isReceiver && displayStatus === 'accepted';
            const showResend = isSent && displayStatus === 'declined' && referral.senderId === currentUserId;
            // Only the sender can cancel, and only while the referral is still pending or accepted.
            const showCancel = isSent && referral.senderId === currentUserId
              && (referral.status === 'pending' || referral.status === 'accepted');
            const hasAnyItem = showEdit || showHide || showUnhide || showReopen || showChangeResponse || showResend || showCancel;
            if (!hasAnyItem) return null;
            return (
              <div className="absolute right-14 top-4 z-10">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-8 h-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-white/50"
                      title="Actions"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {showEdit && (
                      <DropdownMenuItem
                        onClick={() => setIsEditModalOpen(true)}
                        className="cursor-pointer"
                      >
                        <Edit className="w-4 h-4 mr-2 text-blue-600" />
                        <span>Edit Referral</span>
                      </DropdownMenuItem>
                    )}
                    {showChangeResponse && (
                      <DropdownMenuItem
                        onClick={() => setShowChangeResponseModal(true)}
                        className="cursor-pointer text-amber-700 focus:text-amber-700 focus:bg-amber-50"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        <span>Change Response</span>
                      </DropdownMenuItem>
                    )}
                    {showResend && (
                      <DropdownMenuItem
                        onClick={() => setShowResendModal(true)}
                        className="cursor-pointer text-blue-700 focus:text-blue-700 focus:bg-blue-50"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        <span>Resend to Another Partner</span>
                      </DropdownMenuItem>
                    )}
                    {showReopen && (
                      <DropdownMenuItem
                        onClick={() => handleStatusUpdate("pending")}
                        disabled={updateStatusMutation.isPending}
                        className="cursor-pointer text-green-600 focus:text-green-600 focus:bg-green-50"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        <span>Reopen Referral</span>
                      </DropdownMenuItem>
                    )}
                    {showCancel && (
                      <DropdownMenuItem
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={updateStatusMutation.isPending}
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <X className="w-4 h-4 mr-2" />
                        <span>Cancel Referral</span>
                      </DropdownMenuItem>
                    )}
                    {showHide && (
                      <DropdownMenuItem
                        onClick={() => setShowHideConfirm(true)}
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <EyeOff className="w-4 h-4 mr-2" />
                        <span>Hide Referral</span>
                      </DropdownMenuItem>
                    )}
                    {showUnhide && (
                      <DropdownMenuItem
                        onClick={() => setShowUnhideConfirm(true)}
                        className="cursor-pointer text-green-600 focus:text-green-600 focus:bg-green-50"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        <span>Unhide Referral</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })()}

          {/* All badges: Status + Sent/Received + Urgency + Job Types */}
          {/* pr-20 ensures badges don't overlap with hamburger menu and close button on mobile */}
          <div className="flex flex-wrap items-center gap-2 mb-3 pr-20">
            <Badge
              className={`border text-xs ${getStatusBadgeStyle(displayStatus)}`}
              title={isShowingGroupStatus ? `Group Status: ${displayStatus}` : undefined}
            >
              {displayStatus?.charAt(0).toUpperCase() + displayStatus?.slice(1)}
            </Badge>
            <Badge className={isSent
              ? "bg-blue-100 text-blue-700 border border-blue-200 text-xs"
              : "bg-green-100 text-green-700 border border-green-200 text-xs"
            }>
              {isSent ? "Sent" : "Received"}
            </Badge>
            {referral.urgencyLevel && (
              <Badge className={`border text-xs inline-flex items-center gap-1 ${getUrgencyBadgeStyle(referral.urgencyLevel)}`}>
                {getUrgencyIcon(referral.urgencyLevel)}
                {getUrgencyLabel(referral.urgencyLevel)}
              </Badge>
            )}
            {referral.trades && referral.trades.map((trade: string) => (
              <Badge key={trade} className="bg-gray-100 text-gray-600 border border-gray-200 text-xs">{trade}</Badge>
            ))}
            {referral.customTrades && referral.customTrades.map((ct: string) => (
              <Badge key={ct} className="bg-indigo-100 text-indigo-600 border border-indigo-200 text-xs">{ct}</Badge>
            ))}
          </div>

          {/* Client name */}
          <h2 className="text-xl font-bold text-gray-900 text-center">{clientName}</h2>
          <p className="text-sm text-gray-500 text-center mt-1">
            {new Date(referral.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            {' at '}
            {new Date(referral.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
          </p>
        </div>

        {/* ── Scrollable body ── */}
        <div ref={modalBodyRef} className="overflow-y-auto flex-1 bg-white">
          <div className="p-5 space-y-5">

            {/* Status & Date + Referred To (only for non-group referrals) */}
            {!groupId && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Status & Date</p>
                  <Badge
                    className={`border mb-2 ${getStatusBadgeStyle(displayStatus)}`}
                    title={isShowingGroupStatus ? `Group Status: ${displayStatus}` : undefined}
                  >
                    {displayStatus?.charAt(0).toUpperCase() + displayStatus?.slice(1)}
                  </Badge>
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                    {new Date(referral.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
                    {isSent && !referral.receiverId && referral.invitationToken
                      ? "Sent Invitation Link To"
                      : isSent ? "Referred To" : "Referred By"}
                  </p>
                  {isSent && !referral.receiverId && referral.invitationToken ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="w-10 h-10 border border-gray-200 flex-shrink-0">
                        <AvatarFallback className="bg-blue-50 text-blue-600 text-sm font-medium">
                          {(referral.invitedName || "I").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{referral.invitedName || "Invited User"}</p>
                        <p className="text-xs text-blue-600 truncate">Pending acceptance</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={openPartnerProfile}
                        disabled={!partnerId || partnerDeleted}
                        aria-label={
                          partnerDeleted
                            ? "This account has been deleted"
                            : `View ${partnerName || "partner"}'s profile`
                        }
                        className="rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:opacity-80 transition-opacity disabled:cursor-default disabled:hover:opacity-100"
                      >
                        <Avatar className="w-10 h-10 border border-gray-200 flex-shrink-0">
                          <AvatarImage src={profileImageUrl} alt={partnerName || "Partner"} />
                          <AvatarFallback className="bg-gray-100 text-gray-600 text-sm font-medium">
                            {(partnerName || "P").charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                      <div className="min-w-0">
                        <p
                          className={`text-sm font-semibold text-gray-900 truncate ${partnerId && !partnerDeleted ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                          onClick={openPartnerProfile}
                        >
                          {partnerName || "Trade Professional"}
                        </p>
                        {partnerDeleted ? (
                          <p className="text-xs text-gray-500 truncate">
                            {partnerFormerCompany
                              ? `Deleted account - formerly ${partnerFormerCompany}`
                              : "Deleted account"}
                          </p>
                        ) : (
                          partnerTrade && <p className="text-xs text-gray-500 truncate">{partnerTrade}</p>
                        )}
                        <button
                          type="button"
                          onClick={handleReferralChatClick}
                          aria-label={
                            unreadChatCount > 0
                              ? `Referral Chat, ${unreadChatCount} unread message${unreadChatCount !== 1 ? 's' : ''}`
                              : "Referral Chat"
                          }
                          className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                        >
                          <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                          <span>Referral Chat</span>
                          {unreadChatCount > 0 && (
                            <span
                              data-testid="referral-chat-unread-badge"
                              aria-hidden="true"
                              className="ml-0.5 min-w-[1.125rem] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-[1.125rem] text-white"
                            >
                              {unreadChatCount > 99 ? '99+' : unreadChatCount}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick reply: offered only right after the receiver accepts in this session (local state). */}
            {!groupId && isReceiver && referral.status === 'accepted' && quickReply && quickReply.referralId === referral.id && (
              <section
                data-testid="accepted-quick-reply"
                aria-labelledby={quickReply.phase === 'offer' ? 'accepted-quick-reply-title' : undefined}
                className="rounded-xl border border-green-200 bg-green-50 p-4"
              >
                {quickReply.phase === 'sent' ? (
                  <p role="status" className="flex items-start gap-2 text-sm text-green-800">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="min-w-0 break-words">Sent: {quickReply.text}</span>
                  </p>
                ) : (
                  <>
                    <p id="accepted-quick-reply-title" className="flex items-center gap-2 text-sm font-semibold text-green-900">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                      Referral accepted
                    </p>
                    <p className="mt-1 text-sm text-green-800">Want to send your partner a quick reply?</p>
                    <div className="mt-3 flex flex-col gap-2">
                      {QUICK_REPLY_PRESETS.map((text) => (
                        <button
                          key={text}
                          type="button"
                          onClick={() => handleQuickReply(text)}
                          className="min-h-[44px] w-full rounded-full border border-green-600 bg-white px-4 py-2 text-sm font-medium text-green-800 hover:bg-green-100 transition-colors"
                        >
                          {text}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => scrollToReferralChat({ focusInput: true })}
                        className="min-h-[44px] w-full rounded-full border border-green-600 bg-white px-4 py-2 text-sm font-medium text-green-800 hover:bg-green-100 transition-colors"
                      >
                        Write my own…
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setQuickReply(null)}
                      className="mt-2 w-full py-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Not now
                    </button>
                  </>
                )}
              </section>
            )}

            {/* Sender visibility: what has happened on the receiver's side. "Viewed" only means
                the receiver opened it in the app; reminders say "sent", never delivered or seen. */}
            {!groupId && isSent && referral.receiverId && (() => {
              const timeAgo = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });
              const exact = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
              const statusName = referral.status ? referral.status.charAt(0).toUpperCase() + referral.status.slice(1) : '';
              const lines = [
                { key: 'sent', Icon: Send, text: 'Sent', at: referral.createdAt },
                { key: 'viewed', Icon: Eye, text: `Viewed by ${partnerName || 'your partner'}`, at: referral.receiverFirstViewedAt },
                { key: 'reminder', Icon: Bell, text: 'Reminder sent by push', at: referral.lastReminderSentAt },
                { key: 'accepted', Icon: CheckCircle, text: 'Accepted', at: referral.acceptedAt },
                // The acceptance line already covers a status change that was the acceptance.
                referral.statusUpdatedAt !== referral.acceptedAt
                  ? { key: 'status', Icon: RotateCcw, text: `Status changed to ${statusName}`, at: referral.statusUpdatedAt }
                  : null,
              ].filter((line): line is { key: string; Icon: typeof Send; text: string; at: string } => !!line?.at)
                // Newest first. Ties keep the later step in the sequence above first.
                .map((line, index) => ({ line, index }))
                .sort((a, b) => new Date(b.line.at).getTime() - new Date(a.line.at).getTime() || b.index - a.index)
                .map(({ line }) => line);
              const visibleLines = showAllActivity ? lines : lines.slice(0, 1);
              return (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Activity</p>
                  {referral.status?.toLowerCase() === 'pending' && (
                    <p className="flex items-center gap-1.5 text-sm font-medium text-yellow-700 mb-1.5">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                      Awaiting response
                    </p>
                  )}
                  <ul className="space-y-1">
                    {visibleLines.map(({ key, Icon, text, at }) => (
                      <li key={key} className="flex items-start gap-1.5 text-xs text-gray-600">
                        <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-px text-gray-400" />
                        <span className="min-w-0" title={exact(at)}>{text}, {timeAgo(at)}</span>
                      </li>
                    ))}
                  </ul>
                  {lines.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setShowAllActivity((open) => !open)}
                      aria-expanded={showAllActivity}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      {showAllActivity ? 'Hide activity' : 'Show all activity'}
                      {showAllActivity ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Group Information (replaces Status & Referred To for group referrals) */}
            {groupId && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Group Referral</p>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-purple-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        Sent to {groupDetails?.referrals?.length || '...'} partners
                      </p>
                      <p className="text-xs text-gray-500">
                        {groupDetails?.group?.isSingleAccept ? 'First to accept gets the job' : 'Multiple partners can accept'}
                      </p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs">
                      Group
                    </Badge>
                  </div>

                  {isLoadingGroup ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">Loading group details...</span>
                    </div>
                  ) : groupDetails?.referrals && groupProgress ? (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-500 mb-2">
                        {groupProgress.progressText}
                      </div>

                      {/* Progress bar */}
                      <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${groupProgress.progressPercent}%` }}
                        />
                      </div>

                      {/* Partner list */}
                      <div className="space-y-2 pt-2 border-t border-gray-100">
                        {groupDetails.referrals.map((ref: any) => (
                          <div key={ref.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${
                                ref.statusName === 'completed' ? 'bg-blue-500' :
                                ref.statusName === 'accepted' ? 'bg-green-500' :
                                ref.statusName === 'declined' ? 'bg-red-500' :
                                ref.statusName === 'on_hold' ? 'bg-amber-500' :
                                'bg-gray-300'
                              }`} />
                              <span className="text-gray-700">{ref.receiverName}</span>
                            </div>
                            <Badge className={`text-xs px-1.5 py-0 ${getStatusBadgeStyle(ref.statusName)}`}>
                              {ref.statusName?.charAt(0).toUpperCase() + ref.statusName?.slice(1).replace('_', ' ')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Closure Information - Show only when referral is closed */}
            {(referral.status === 'closed' || referral.statusName === 'closed') && (referral.completionNotes || referral.outcome) && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Closure Information</p>
                <div className="bg-amber-50 rounded-xl border border-amber-200 overflow-hidden">
                  <div className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-amber-900 mb-1">Referral Closed</p>

                        {/* Display outcome/reason if available */}
                        {referral.outcome && (
                          <div className="mb-2">
                            <span className="text-xs font-medium text-amber-700">Reason: </span>
                            <span className="text-sm text-amber-800 font-medium">
                              {referral.outcome === 'no_move_forward' ? "Customer didn't move forward" :
                               referral.outcome === 'no_contact' ? "Could not reach customer" :
                               referral.outcome === 'not_fit' ? "Not a good fit" :
                               referral.outcome === 'other' ? "Other reason" :
                               referral.outcome === 'completed' ? "Completed successfully" :
                               referral.outcome}
                            </span>
                          </div>
                        )}

                        {/* Display notes if available */}
                        {referral.completionNotes && (
                          <div>
                            {referral.outcome && <span className="text-xs font-medium text-amber-700">Notes: </span>}
                            <p className="text-sm text-amber-800 whitespace-pre-wrap leading-relaxed">
                              {referral.completionNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Contact Information</p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                {/* Name */}
                <div className="flex items-center px-4 py-3 gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400">Name</p>
                    <p className="text-sm font-semibold text-gray-900">{clientName}</p>
                  </div>
                </div>
                {/* Phone */}
                {referral.clientPhone && (
                  <div className="flex items-center px-4 py-3 gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400">Phone</p>
                      <p className="text-sm font-semibold text-gray-900">{referral.clientPhone}</p>
                    </div>
                    <a href={`tel:${referral.clientPhone}`} className="flex-shrink-0 text-xs font-semibold text-green-700 bg-transparent hover:bg-gray-50 hover:text-green-800 px-3 py-1.5 rounded-lg transition-colors border border-gray-300">Call</a>
                  </div>
                )}
                {/* Email */}
                {referral.clientEmail && (
                  <div className="flex items-center px-4 py-3 gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400">Email</p>
                      <p className="text-sm font-semibold text-gray-900 truncate">{referral.clientEmail}</p>
                    </div>
                    <a href={`mailto:${referral.clientEmail}`} className="flex-shrink-0 text-xs font-semibold text-pink-500 bg-transparent hover:bg-gray-50 hover:text-pink-600 px-3 py-1.5 rounded-lg transition-colors border border-gray-300">Email</a>
                  </div>
                )}
                {/* Location */}
                {referral.jobLocation && (
                  <div className="flex items-center px-4 py-3 gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400">Location</p>
                      <p className="text-sm font-semibold text-gray-900">{referral.jobLocation}</p>
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(referral.jobLocation)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-shrink-0 text-xs font-semibold text-orange-500 bg-transparent hover:bg-gray-50 hover:text-orange-600 px-3 py-1.5 rounded-lg transition-colors border border-gray-300"
                    >
                      Map
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Job Description */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Job Description</p>
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <p className={`text-sm text-gray-900 whitespace-pre-wrap leading-relaxed${showFullDescription ? '' : ' line-clamp-1'}`}>
                  {referral.jobDescription || "No description provided"}
                </p>
                {referral.jobDescription && referral.jobDescription.length > 60 && (
                  <button
                    onClick={() => setShowFullDescription(v => !v)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors mt-2"
                  >
                    {showFullDescription ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            </div>

            {/* Additional Notes */}
            {referral.notes && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Additional Notes</p>
                <div className="bg-gray-50 rounded-xl border border-gray-200 border-l-4 border-l-amber-400 p-4">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{referral.notes}</p>
                </div>
              </div>
            )}

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Attachments</p>
                <p className="text-xs text-gray-400">Shared with both parties</p>
              </div>
              {/* Thumbnail grid for existing attachments */}
              {attachmentsList.length > 0 && (() => {
                const hasOverflow = attachmentsList.length > MAX_COLS;
                const needsOverflow = !showAllAttachments && hasOverflow;
                const visibleItems = needsOverflow
                  ? attachmentsList.slice(0, MAX_COLS - 1)
                  : attachmentsList;
                const hiddenCount = attachmentsList.length - (MAX_COLS - 1);

                const renderThumbnail = (attachment: any, index: number) => {
                  const url = attachmentUrls[index];
                  const isImage = attachment.contentType?.startsWith('image/') ||
                    /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(attachment.name || '');
                  const isVideo = attachment.contentType?.startsWith('video/') ||
                    /\.(mp4|mov|avi|webm|mkv|m4v)$/i.test(attachment.name || '');

                  return (
                    <div
                      key={index}
                      className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200"
                    >
                      <button
                        onClick={() => handleAttachmentClick(index)}
                        className="w-full h-full hover:opacity-90 transition-opacity"
                      >
                        {url && isImage && (
                          <img src={url} alt={attachment.name} className="w-full h-full object-cover" />
                        )}
                        {url && isVideo && (
                          <video src={url} className="w-full h-full object-cover" muted playsInline />
                        )}
                        {(!url || (!isImage && !isVideo)) && (
                          <div className="w-full h-full flex items-center justify-center">
                            <Paperclip className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1.5 py-1">
                          <p className="text-white text-[10px] truncate">{attachment.name || `File ${index + 1}`}</p>
                        </div>
                      </button>
                    </div>
                  );
                };

                return (
                  <>
                    <div
                      className="grid gap-2 mb-3"
                      style={{ gridTemplateColumns: `repeat(${MAX_COLS}, 1fr)` }}
                    >
                      {visibleItems.map((attachment: any, index: number) => renderThumbnail(attachment, index))}
                      {needsOverflow && (
                        <button
                          onClick={() => setShowAllAttachments(true)}
                          className="w-full aspect-square rounded-xl overflow-hidden bg-gray-200 border border-gray-300 hover:bg-gray-300 transition-colors flex flex-col items-center justify-center gap-0.5"
                        >
                          <span className="text-lg font-bold text-gray-600">+{hiddenCount}</span>
                          <span className="text-[10px] text-gray-500">more</span>
                        </button>
                      )}
                      {hasOverflow && showAllAttachments && (
                        <button
                          onClick={() => setShowAllAttachments(false)}
                          className="w-full aspect-square rounded-xl overflow-hidden bg-gray-200 border border-gray-300 hover:bg-gray-300 transition-colors flex flex-col items-center justify-center gap-0.5"
                        >
                          <span className="text-lg font-bold text-gray-600">−</span>
                          <span className="text-[10px] text-gray-500">less</span>
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Upload button — always visible */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || attachmentsList.length >= 20}
                className="w-full bg-gray-50 border border-dashed border-gray-300 rounded-xl p-4 flex items-center justify-center gap-2 text-gray-500 hover:bg-gray-100 hover:border-gray-400 transition-colors disabled:opacity-50"
              >
                {isUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Uploading...</span></>
                ) : attachmentsList.length >= 20 ? (
                  <><ImagePlus className="w-4 h-4" /><span className="text-sm">Maximum files reached (20)</span></>
                ) : (
                  <><ImagePlus className="w-4 h-4" /><span className="text-sm">Add photos or videos</span></>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={MEDIA_ACCEPT_ATTRIBUTE}
                multiple
                className="hidden"
                onChange={handleFileUpload}
              />
              {/* Native camera (Card 20 task B). The picker above stays the way to
                  attach videos and multiple files — @capacitor/camera returns exactly
                  one image — so this is an additional action, not a replacement. */}
              {isNativeCaptureAvailable() && (
                <button
                  type="button"
                  onClick={() =>
                    void captureImageInto(
                      (file) => uploadSelectedFiles([file]),
                      showError,
                      { source: 'camera' },
                    )
                  }
                  disabled={isUploading || attachmentsList.length >= 20}
                  className="w-full mt-2 bg-white border border-gray-300 rounded-xl p-4 flex items-center justify-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Camera className="w-4 h-4" />
                  <span className="text-sm">Take a photo</span>
                </button>
              )}
              <p className="text-xs text-gray-400 mt-1.5">Both partners can add files. Max 20 files ({attachmentsList.length}/20).</p>
              <p className="text-xs text-gray-400 mt-0.5">Max video size: 250MB. This is usually 2–3 minutes on standard phone settings, less if recording 4K/60fps.</p>
            </div>

            {/* Messages */}
            <div ref={setChatSection}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Messages</p>
                <p className="text-xs text-gray-400">{threadMessages.length} message{threadMessages.length !== 1 ? 's' : ''}</p>
              </div>

              {/* Partner tabs for group referrals */}
              {groupId && groupDetails?.referrals && groupDetails.referrals.length > 0 && (
                <div className="flex gap-1 mb-3 overflow-x-auto">
                  {groupDetails.referrals.map((ref: any, index: number) => (
                    <button
                      key={ref.id}
                      onClick={() => setSelectedPartnerIndex(index)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                        selectedPartnerIndex === index
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {ref.receiverName}
                    </button>
                  ))}
                </div>
              )}

              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                {threadMessages.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">
                    No messages in this {groupId ? 'conversation' : 'referral thread'} yet.
                  </p>
                ) : (
                  <div ref={messagesContainerRef} onScroll={handleThreadScroll} className="overflow-y-auto max-h-[300px] p-3 space-y-2">
                    {threadMessages.map((msg: any) => (
                      <div key={msg.id} className={`flex ${msg.isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] ${msg.isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                          {!msg.isOwn && (
                            <p className="text-[10px] font-medium text-gray-500 mb-0.5 px-1">{msg.senderName}</p>
                          )}
                          {editingMessageId === msg.id ? (
                            <div className="w-full min-w-[200px] space-y-1.5">
                              <textarea
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                rows={2}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (editContent.trim()) {
                                      editMessageMutation.mutate({ messageId: msg.id, content: editContent.trim() });
                                    }
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingMessageId(null);
                                    setEditContent('');
                                  }
                                }}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                              />
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={() => { setEditingMessageId(null); setEditContent(''); }}
                                  className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => {
                                    if (editContent.trim()) {
                                      editMessageMutation.mutate({ messageId: msg.id, content: editContent.trim() });
                                    }
                                  }}
                                  disabled={!editContent.trim() || editMessageMutation.isPending}
                                  className="px-3 py-1 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className={`flex items-center gap-1 ${msg.isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
                              <div className={`rounded-2xl px-3 py-1.5 text-sm ${msg.isOwn ? 'bg-primary text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`}>
                                {msg.content}
                              </div>
                              {msg.isOwn && !msg.pending && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      aria-label="Message actions"
                                      className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                                    >
                                      <MoreVertical className="w-3.5 h-3.5" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="min-w-[120px]">
                                    <DropdownMenuItem
                                      onClick={() => { setEditingMessageId(msg.id); setEditContent(msg.content); }}
                                    >
                                      <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => deleteMessageMutation.mutate(msg.id)}
                                      className="text-red-600 focus:text-red-600"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          )}
                          <p className="text-[10px] text-gray-400 mt-0.5 px-1">
                            {new Date(msg.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {msg.editedAt && <span className="italic"> · edited</span>}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}

                {/* Message input */}
                <div className="border-t border-gray-200 p-3">
                  <div className="flex gap-2">
                    <input
                      ref={chatInputRef}
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleMessageKeyPress}
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      disabled={sendMessageMutation.isPending}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendMessageMutation.isPending}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-4 h-4" />
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Completed verification details */}
            {referral.status === "completed" && referral.completedAt && referral.jobCompletedVerified && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold text-sm">Referral Completed & Verified</span>
                </div>
                {referral.jobCompletedVerified && (
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <Check className="w-4 h-4" /><span>Job completed</span>
                  </div>
                )}
                {referral.customerSatisfiedVerified && (
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <Check className="w-4 h-4" /><span>Customer satisfied</span>
                  </div>
                )}
                {referral.completionNotes && (
                  <p className="text-sm text-gray-700 italic pt-2 border-t border-green-200">"{referral.completionNotes}"</p>
                )}
              </div>
            )}

          </div>
        </div>

        {/* ── Sticky bottom bar ── */}
        {/* Only show if there's actual content to display */}
        {(
          // Sender's Cancel Referral lives in the header's ⋮ menu, so the sender gets no bar
          // for pending or accepted referrals.
          // Show for pending if user is receiver (accept/decline)
          (referral.status === 'pending' && !isSent && referral.receiverId === currentUserId) ||
          // Show for accepted if user is the receiver (close out / change response)
          (referral.status === 'accepted' && !isSent && !isOrgMember && referral.receiverId === currentUserId) ||
          // Show for declined if user is sender (resend action)
          (referral.status === 'declined' && isSent && referral.senderId === currentUserId) ||
          // Show for completed only if there's revenue to display
          (referral.status === 'completed' && !isSent && referral.revenue && Number(referral.revenue) > 0)
        ) && (
          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-5 py-3">
            {/* Pending (received by current user): Accept/Decline actions */}
            {referral.status === 'pending' && !isSent && referral.receiverId === currentUserId && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => handleStatusUpdate("accepted")}
                    disabled={updateStatusMutation.isPending}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 rounded-xl flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Accept Referral
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleStatusUpdate("declined")}
                    disabled={updateStatusMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 text-red-600 border-red-200 hover:bg-red-50 font-medium py-2.5 rounded-xl"
                  >
                    <X className="w-4 h-4" />
                    Decline
                  </Button>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  Accepting means you will follow up with the customer. It does not mean the job is booked.
                </p>
              </div>
            )}
            {/* Accepted: Close Out / Change Response (receiver only). The sender cancels from the ⋮ menu. */}
            {referral.status === 'accepted' && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {!isSent && !isOrgMember && referral.receiverId === currentUserId && (
                  <>
                    <Button
                      onClick={() => setShowCompletionModal(true)}
                      className="flex-1 min-w-0 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5 rounded-xl flex items-center justify-center gap-2"
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      Close Out Referral
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowChangeResponseModal(true)}
                      className="flex-1 min-w-0 flex items-center justify-center gap-2 text-red-600 border-red-200 hover:bg-red-50 font-medium py-2.5 rounded-xl"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Change Response
                    </Button>
                  </>
                )}
              </div>
            )}
            {/* Declined (sent by current user): Resend to another partner */}
            {referral.status === 'declined' && isSent && referral.senderId === currentUserId && (
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setShowResendModal(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Resend to Another Partner
                </Button>
              </div>
            )}
            {/* Completed: badge + income amount */}
            {referral.status === 'completed' && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">Completed</span>
                  <span className="text-xs text-gray-400">
                    {new Date(referral.completedAt || referral.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
                {!isSent && referral.revenue && Number(referral.revenue) > 0 && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">
                      {formatValue(referral.revenue)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </DialogContent>

      <ReferralCompletionModal
        referral={{ id: referral.id, clientName: clientName, senderName: referral.senderName }}
        isOpen={showCompletionModal}
        onClose={() => { setShowCompletionModal(false); onClose(); }}
      />

      <ChangeReferralResponseModal
        referral={{ id: referral.id, clientName: clientName, senderName: referral.senderName }}
        isOpen={showChangeResponseModal}
        onClose={() => { setShowChangeResponseModal(false); }}
      />

      <ResendReferralModal
        referral={referral}
        isOpen={showResendModal}
        onClose={() => { setShowResendModal(false); }}
      />

      <AttachmentViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        attachments={attachmentsList}
        attachmentUrls={attachmentUrls}
        initialIndex={viewerInitialIndex}
        onShare={handleShareAttachment}
        onDelete={(attachment) => setAttachmentPendingDelete(attachment)}
        canDelete={canDeleteAttachment}
      />

      {/* Cancel Referral Confirmation Dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Referral?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this referral to {clientName}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Referral</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleStatusUpdate("canceled");
                setShowCancelConfirm(false);
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Yes, Cancel Referral
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hide Referral Confirmation Dialog */}
      <AlertDialog open={showHideConfirm} onOpenChange={setShowHideConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide Referral</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to hide this referral from your dashboard? This will only affect your view - the other party will still see it.
              Note: This will not affect your monthly referral stats or dashboard metrics.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                hideMutation.mutate(referral.id);
                setShowHideConfirm(false);
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Hide
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unhide Referral Confirmation Dialog */}
      <AlertDialog open={showUnhideConfirm} onOpenChange={setShowUnhideConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unhide Referral</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to restore this referral to your normal view? It will appear in your referral list again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                unhideMutation.mutate(referral.id);
                setShowUnhideConfirm(false);
              }}
              className="bg-green-600 hover:bg-green-700 focus:ring-green-600"
            >
              Unhide
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Attachment Confirmation Dialog */}
      <AlertDialog
        open={!!attachmentPendingDelete}
        onOpenChange={(open) => { if (!open) setAttachmentPendingDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attachment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{attachmentPendingDelete?.name ? ` "${attachmentPendingDelete.name}"` : ' this file'}? This permanently removes the file for both parties and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAttachmentMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (attachmentPendingDelete) deleteAttachmentMutation.mutate(attachmentPendingDelete);
              }}
              disabled={deleteAttachmentMutation.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteAttachmentMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Referral Modal */}
      <EditReferralModal
        referral={referral}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />

      {/* Partner Profile Preview — opens when the partner name or avatar is
          clicked. Renders immediately with a stub built from referral fields
          so the modal isn't blocked on the /api/users/:id/public fetch. */}
      {profilePreviewPartnerId && (
        <ProfilePreviewModal
          isOpen={showProfilePreview}
          onClose={() => {
            setShowProfilePreview(false);
            setProfilePreviewPartnerId(null);
          }}
          partner={((): any => {
            const base = partnerProfileData
              ? toProfilePreviewPartner(partnerProfileData)
              : {
                  id: profilePreviewPartnerId,
                  firstName: partnerName?.split(' ')[0] ?? '',
                  lastName: partnerName?.split(' ').slice(1).join(' ') ?? '',
                  profileImageUrl,
                  trigPrimaryTradeName: partnerTrade,
                  status: 'connected' as const,
                };
            return {
              ...base,
              connectionId: partnerConnection.connectionId,
              isFavorite: partnerConnection.isFavorite,
            };
          })()}
          currentUserOrganizationId={(user as any)?.organizationId}
          // Referral counterparties are connected partners — gate visibility
          // on the partner shape (which always carries status='connected'
          // here) so the star renders immediately, and gate the action on
          // the connectionId being loaded.
          showFavoriteButton={true}
          showMenuButton={true}
          onToggleFavorite={
            partnerConnection.connectionId ? partnerConnection.toggleFavorite : undefined
          }
          showActionButtons={true}
          onSendReferral={() => {
            if (!profilePreviewPartnerId) return;
            setShowProfilePreview(false);
            setProfilePreviewPartnerId(null);
            navigate(`/send-referral?partnerId=${profilePreviewPartnerId}`);
          }}
          onSendMessage={() => {
            if (!profilePreviewPartnerId) return;
            setShowProfilePreview(false);
            setProfilePreviewPartnerId(null);
            navigate('/messages', {
              state: {
                autoSelectPartnerId: profilePreviewPartnerId,
              },
            });
          }}
          onPartnerRemoved={() => {
            queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections'] });
            setShowProfilePreview(false);
            setProfilePreviewPartnerId(null);
          }}
        />
      )}
    </Dialog>
  );
}
